import React, { useState } from "react";
import useEditTraining from "../hooks/useEditTraining";
import useUploadFiles from "../../../hooks/useUploadFiles";

// components
import Upload from "../../../component/modals/upload";
import DateInput from "../../../component/element/forms/DateInput";
import RichTextEditor from "../../../component/element/forms/RichTextEditor";
import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";

// state
import { useAppDispatch, useAppSelector } from "../../../store";
import { get_training, edit } from "../../../services/apis/training";
import { get_countries, get_states } from "../../../services/apis/locations";
import { clearStates } from "../../../store/slices/locations";
import Button from "../../../component/element/forms/Button";
import { useNavigate, useParams } from "react-router-dom";
// import { loading } from "../../../store/slices/loading";

// type IEditTraining = {
//   id: string;
//   onHandler: () => void;
// };

const EditTraining = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [imgPath, setImgPath] = useState("");

  const states = useAppSelector(({ locations }) => locations.states);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const loading = useAppSelector(({ training }) => training.loading);
  const { data, dataSet, error, errorSet, onValidate } = useEditTraining();

  const { loader, uploadFile, deleteFiles } = useUploadFiles();

  const handleOk = async () => {
    const validate = onValidate();
    if (!validate) return;

    const updated = await edit({ ...data, id: id ?? data.id }, dispatch);
    if (updated) navigate(-1);
  };

  const onLoad = React.useCallback(() => {
    if (id) {
      get_training(dispatch, id);
      get_countries(dispatch);
    }
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  React.useEffect(() => {
    if (data.country_id) {
      get_states(dispatch, data.country_id);
    }
  }, [dispatch, data.country_id]);

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-md">
      <div className="">
        {/* <img src={image} alt="Edit Training" className="w-11 h-auto" /> */}
      </div>

      <Upload
        title="Upload Image"
        label="JPG & PNG"
        name="image"
        loading={loader}
        status=""
        completed={!!data.image}
        imgPreview={data.image} // This should show the existing image
        onChange={async (fileOrFiles) => {
          const paths = await uploadFile(fileOrFiles as File, "trainings");
          if (paths.length > 0) {
            const img_path = paths[0].base_url + "/" + paths[0].path;
            dataSet((prev) => ({ ...prev, image: img_path }));
            errorSet((prev) => ({ ...prev, image: "" }));
            setImgPath(paths[0].path);
          }
        }}
        onDelete={async () => {
          if (imgPath) {
            const res = await deleteFiles(imgPath);
            if (res) {
              dataSet((prev) => ({ ...prev, image: "" }));
              errorSet((prev) => ({ ...prev, image: "" }));
            }
          }
        }}
      />

      <TextInput
        label="Title"
        name="name"
        placeholder="Title"
        value={data.name}
        type="text"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { value, name } = e.target;
          dataSet((prev) => ({ ...prev, [name]: value }));
          errorSet((prev) => ({ ...prev, [name]: "" }));
        }}
      />

      <TextInput
        label="Address"
        name="address"
        placeholder="Address"
        value={data.address}
        type="text"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { value, name } = e.target;
          dataSet((prev) => ({ ...prev, [name]: value }));
          errorSet((prev) => ({ ...prev, [name]: "" }));
        }}
      />

      <div className="grid grid-cols-2 gap-6">
        <TextInput
          label="Link"
          name="link"
          placeholder="Link"
          value={data.link}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { value, name } = e.target;
            dataSet((prev) => ({ ...prev, [name]: value }));
            errorSet((prev) => ({ ...prev, [name]: "" }));
          }}
        />

        <TextInput
          label="Amount"
          name="amount"
          placeholder="Amount"
          value={data.amount}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { value, name } = e.target;
            dataSet((prev) => ({ ...prev, [name]: value }));
            errorSet((prev) => ({ ...prev, [name]: "" }));
          }}
        />

        <TextInput
          label="Duration"
          name="duration"
          placeholder="Duration"
          value={data.duration}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { value, name } = e.target;
            dataSet((prev) => ({ ...prev, [name]: value }));
            errorSet((prev) => ({ ...prev, [name]: "" }));
          }}
        />

        <DateInput
          label="Date"
          value={data.date}
          error={error.date ? "Date is required" : undefined}
          onChange={(e) => {
            dataSet((prev) => ({ ...prev, date: e }));
            errorSet((prev) => ({ ...prev, date: "" }));
          }}
        />
        <SelectOption
          items={countries}
          label="Country"
          placeholder="Country"
          value={data.country_id}
          onChange={(e) => {
            dataSet((prev) => ({ ...prev, country_id: e, state_id: "" }));
            errorSet((prev) => ({
              ...prev,
              country_id: "",
              state_id: "",
            }));
            dispatch(clearStates());
          }}
        />

        <SelectOption
          items={states}
          label="State"
          placeholder="State"
          value={data.state_id}
          onChange={(e) => {
            dataSet((prev) => ({ ...prev, state_id: e }));
            errorSet((prev) => ({ ...prev, state_id: "" }));
          }}
        />
      </div>

      <div>
        <RichTextEditor
          label="Description"
          value={data.body}
          error={error.body ? "Description is required" : undefined}
          onChange={(value) => {
            dataSet((prev) => ({ ...prev, body: value }));
            errorSet((prev) => ({ ...prev, body: "" }));
          }}
        />
      </div>

      <Button
        title="Update"
        type="submit"
        loading={loading}
        onClick={handleOk}
        className="text-white"
      />
    </div>
  );
};

export default EditTraining;
